"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { ImageIcon } from "lucide-react";
import type { BlogPostMeta } from "@/data/blogs";

export type BlogCardProps = {
  post: BlogPostMeta;
  className?: string;
  /**
   * When false, renders no focusable links (for aria-hidden marquee tracks).
   * Mouse navigation still works via click; keyboard users use the sr-only list.
   */
  interactive?: boolean;
};

export function BlogCard({
  post,
  className,
  interactive = true,
}: BlogCardProps) {
  const router = useRouter();
  const [hasError, setHasError] = useState(false);
  const href = `/blogs/${post.slug}`;

  const imageBlock = hasError ? (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-linear-to-br from-[#29361a] to-[#1f2914] text-[#c5d4a8]">
      <ImageIcon className="h-10 w-10" aria-hidden="true" />
      <span className="px-4 text-center text-xs">Image coming soon</span>
    </div>
  ) : (
    <Image
      src={post.imageSrc}
      alt=""
      fill
      sizes="(max-width: 640px) 85vw, (max-width: 1024px) 40vw, 320px"
      quality={70}
      className="object-cover transition-transform duration-500 ease-out group-hover:scale-105"
      onError={() => setHasError(true)}
    />
  );

  const metaBlock = (
    <div className="mt-2 flex flex-wrap items-start justify-between gap-x-3 gap-y-1 text-[12px] font-medium text-[#29361a] sm:text-[13px]">
      <p className="min-w-0">
        <span>{post.author}</span>
        <span className="mx-1 text-[#5c6b4a]">|</span>
        <span>{post.publisher}</span>
      </p>
      <p className="shrink-0 text-[#29361a]">
        <small className="text-[12px] font-medium sm:text-[13px]">
          {post.date}
        </small>
      </p>
    </div>
  );

  const body = (
    <>
      <div className="relative block aspect-4/3 overflow-hidden rounded-[4px] bg-[#e8ebe2] shadow-[0_10px_28px_rgba(41,54,26,0.18)] transition-shadow duration-300 group-hover:shadow-[0_14px_36px_rgba(41,54,26,0.26)]">
        {imageBlock}
      </div>

      <div className="mt-3 flex flex-1 flex-col">
        <h3 className="font-heading text-[15px] font-bold leading-snug tracking-wide text-[#1f2914] transition-colors group-hover:text-[#b46c29] sm:text-base">
          {post.title}
        </h3>
        {metaBlock}
      </div>
    </>
  );

  if (!interactive) {
    return (
      <article
        className={[
          "group flex h-full w-full max-w-[340px] cursor-pointer flex-col sm:max-w-none",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => router.push(href)}
      >
        {body}
      </article>
    );
  }

  return (
    <article
      className={[
        "group flex h-full w-full max-w-[340px] flex-col sm:max-w-none",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <Link
        href={href}
        className="flex h-full flex-col text-inherit no-underline"
      >
        {body}
      </Link>
    </article>
  );
}
